import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Headphones, Gamepad2, Video, Camera } from "lucide-react";
// Real prod leaves: the client mirror canvas + the live-sync hook + audio engine.
import MirrorCanvas from "@/components/bilateral/MirrorCanvas";
import { useBilateralMirror } from "@/hooks/use-bilateral-mirror";
import AudioEngine, { type AudioEngineHandle, type AudioPreset } from "@/components/audio/AudioEngine";
import CheckinForm from "@/components/session/CheckinForm";
import CheckinComparison from "@/components/session/CheckinComparison";
import { DEFAULT_EMOTIONS } from "@/components/session/EmotionPicker";
import { emptyCheckinData, type CheckinActive, type CheckinLiveData } from "@/lib/checkin-types";
import { useGamepadHaptics } from "@/lib/haptics/useGamepadHaptics";
import { cn } from "@/lib/utils";

/**
 * ClientPerspective (Scope A10) — exactly what the client sees on their device.
 * The ball is the REAL MirrorCanvas, driven over the loopback socket by the
 * therapist view in the same tab (no network, no peer). Around it are the REAL
 * permission-priming surfaces a client meets — audio gesture-unlock, controller
 * pairing, and the camera pre-permission card + an ILLUSTRATION of the native
 * browser prompt (which an app can never render itself).
 */
export function ClientPerspective({ sessionCode }: { sessionCode: string }) {
  const m = useBilateralMirror(sessionCode);
  const audioRef = useRef<AudioEngineHandle>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  // The client's video opt-in, exactly as it appears on their screen: a "Join
  // Video" tap → the native camera prompt (shown here as a labelled ILLUSTRATION,
  // since an app can't render the real one) → an on-camera self-view placeholder.
  const [videoStep, setVideoStep] = useState<"idle" | "prompt" | "joined">("idle");

  const { status: hapticsStatus, controllerLabel } = useGamepadHaptics({
    enabled: m.hapticsOn && m.isRunning && audioEnabled, // armed once the client has interacted
    intensity: m.hapticsIntensity,
  });

  // ── Therapist-driven check-in, mirrored over the loopback ─────────────────
  // The client NEVER opens a check-in itself: m.activeCheckin (set by the
  // therapist's checkin_state push) is the only trigger. Same wiring as the real
  // ClientView, minus the backend POST (this demo persists nothing).
  const activeCheckin = m.activeCheckin;
  const [closedPhase, setClosedPhase] = useState<CheckinActive>(null);
  const [submitting, setSubmitting] = useState(false);
  const [liveData, setLiveData] = useState<CheckinLiveData>(() => emptyCheckinData("pre"));
  const [preAnswers, setPreAnswers] = useState<CheckinLiveData | null>(null);
  const broadcastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Submitting (or the therapist dismissing) closes the form locally without
  // waiting for the round-trip; re-opening (null → phase) clears the latch.
  const effectiveCheckin: CheckinActive = activeCheckin && activeCheckin !== closedPhase ? activeCheckin : null;
  useEffect(() => { if (!activeCheckin) setClosedPhase(null); }, [activeCheckin]);

  // (Re)initialise the editable copy when the active form changes + apply inbound
  // therapist edits. Applying inbound here does NOT re-broadcast (no ping-pong).
  useEffect(() => {
    if (!activeCheckin) return;
    if (m.checkinData && m.checkinData.phase === activeCheckin) setLiveData(m.checkinData);
    else setLiveData((prev) => (prev.phase === activeCheckin ? prev : emptyCheckinData(activeCheckin)));
  }, [activeCheckin, m.checkinData]);

  // Keep the pre answers for the post-session comparison whenever pre data arrives.
  useEffect(() => {
    if (m.checkinData?.phase === "pre") setPreAnswers(m.checkinData);
  }, [m.checkinData]);

  useEffect(() => () => { if (broadcastTimer.current) clearTimeout(broadcastTimer.current); }, []);

  // Local edit → update copy immediately, broadcast (debounced) to the therapist.
  const update = useCallback((next: CheckinLiveData) => {
    setLiveData(next);
    if (broadcastTimer.current) clearTimeout(broadcastTimer.current);
    broadcastTimer.current = setTimeout(() => m.broadcastCheckinData(next), 120);
  }, [m]);

  const submitPre = useCallback(() => {
    setPreAnswers(liveData);
    setSubmitting(true);
    m.sendCheckinComplete("pre"); // notify therapist; no backend in the demo
    setSubmitting(false);
    setClosedPhase("pre");
  }, [liveData, m]);
  const submitPost = useCallback(() => {
    setSubmitting(true);
    m.sendCheckinComplete("post");
    setSubmitting(false);
    setClosedPhase("post");
  }, [m]);

  return (
    <>
      {/* PORTALED to <body> in a z-[70] wrapper so the modal escapes the page's z-1
          stacking context (app.css:186) and sits above the z-60 demo banner. */}
      {effectiveCheckin === "pre" && createPortal(
        <div className="relative z-[70]">
          <CheckinForm
            value={liveData}
            onChange={update}
            emotions={DEFAULT_EMOTIONS}
            showGoal={false}
            title="Pre-Session Check-in"
            subtitle="Before we begin"
            liveHint="Shared live with your therapist"
            primaryLabel="I'm Ready"
            onPrimary={submitPre}
            busy={submitting}
          />
        </div>,
        document.body,
      )}
      {effectiveCheckin === "post" && createPortal(
        <div className="relative z-[70]">
          <CheckinForm
            value={liveData}
            onChange={update}
            emotions={DEFAULT_EMOTIONS}
            title="Session Complete"
            subtitle="Post-session check-in"
            liveHint="Shared live with your therapist"
            comparison={preAnswers ? (
              <CheckinComparison title="Pre-Session" goal={preAnswers.goal || null} emotions={preAnswers.emotions} distress={preAnswers.distress} capability={preAnswers.capability} note={preAnswers.note || null} />
            ) : (
              <div className="flex h-full items-center justify-center text-center px-2 py-6">
                <p className="text-[12px] leading-relaxed text-[var(--color-text-ghost)]">No pre-session check-in was recorded.</p>
              </div>
            )}
            primaryLabel="Submit"
            onPrimary={submitPost}
            busy={submitting}
          />
        </div>,
        document.body,
      )}
    <div className="flex flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-void)]">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-navy-mid)] px-5 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-accent-bright)]">
          ◐ Client view · what your client sees on their device
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
          {m.connected ? (m.isRunning ? "Following therapist" : "Paused") : "Waiting…"}
        </span>
      </div>

      {/* The mirrored ball. This frame is EXACTLY what the client sees on their
          device — the audio-unlock prompt and the Join Video button are the real
          affordances they meet. Everything OUTSIDE the frame is the demo teaching you. */}
      <div
        className="relative h-[320px] w-full sm:h-[400px]"
        onClick={!audioEnabled ? () => setAudioEnabled(true) : undefined}
      >
        {/* Boundary badge — makes "this square is the client's screen" unmistakable. */}
        <span className="pointer-events-none absolute left-2.5 top-2.5 z-30 rounded-md border border-[var(--color-border-gold)] bg-[rgba(8,12,20,0.82)] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-gold-soft)]">
          ▟ Client&apos;s screen
        </span>

        <MirrorCanvas
          xNorm={m.xNorm}
          hz={m.hz}
          elapsedSeconds={m.elapsedSeconds}
          ballSize={m.ballSize}
          ballColor={m.ballColor}
          canvasBg="#0D1217"
          isRunning={m.isRunning}
          onPositionChange={(x) => audioRef.current?.updateXNorm(x)}
        />
        {audioEnabled && (
          <AudioEngine ref={audioRef} isRunning={m.isRunning} audioOn={m.audioOn} volume={m.audioVolume} preset={m.audioPreset as AudioPreset} />
        )}

        {/* Center: tap-anywhere-to-enable-audio (🎙️) — the autoplay-policy prompt the
            client actually meets, mirrored from the live ClientView. pointer-events-none
            so the tap falls through to the frame's unlock handler. */}
        {!audioEnabled && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[rgba(8,12,20,0.5)] backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-2 rounded-lg border border-[var(--color-border-gold)] bg-[rgba(13,27,46,0.92)] px-6 py-4 text-center">
              <span className="text-[28px]" aria-hidden>🎙️</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--color-gold-soft)]">Tap anywhere to enable audio</span>
              <span className="max-w-[240px] text-[10px] leading-relaxed text-[var(--color-text-muted)]">Your browser requires a tap before it can play sound — this is that prompt.</span>
            </div>
          </div>
        )}

        {/* Bottom-left: the client's "Join Video" tap — mirrors the live ClientView. */}
        {audioEnabled && videoStep === "idle" && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setVideoStep("prompt"); }}
            className="absolute bottom-3 left-3 z-20 inline-flex items-center gap-2 rounded-md bg-[var(--color-gold)] px-3.5 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            <Video className="h-3.5 w-3.5" /> Join Video
          </button>
        )}

        {/* Join Video raises the native camera prompt — shown as a clearly labelled
            ILLUSTRATION (an app can never render the real dialog). */}
        {videoStep === "prompt" && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-[rgba(8,12,20,0.72)] px-4">
            <div className="relative w-full max-w-[300px] rounded-lg border border-[#3a4a5e] bg-[#202733] p-3.5 text-[#e8eef6] shadow-xl">
              <span className="absolute -top-2 left-3 rounded bg-[var(--color-bg-deep)] px-1.5 font-mono text-[8px] uppercase tracking-wide text-[var(--color-text-ghost)]">Illustration — not a real dialog</span>
              <p className="text-[12px] font-medium">demo.psy180.com wants to</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-[12px]"><Camera className="h-3.5 w-3.5" /> Use your camera</p>
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={(e) => { e.stopPropagation(); setVideoStep("idle"); }} className="rounded border border-[#56627a] px-3 py-1 text-[11px]">Block</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setVideoStep("joined"); }} className="rounded bg-[#3b82f6] px-3 py-1 text-[11px] font-medium text-white">Allow</button>
              </div>
            </div>
          </div>
        )}

        {/* Joined: a self-view PiP placeholder, bottom-left (where the client's camera sits). */}
        {videoStep === "joined" && (
          <div className="absolute bottom-3 left-3 z-20 flex h-[64px] w-[88px] flex-col items-center justify-center gap-1 rounded-md border border-[var(--color-border-gold)] bg-[rgba(13,27,46,0.92)]">
            <Video className="h-4 w-4 text-[var(--color-gold-soft)]" />
            <span className="font-mono text-[8px] uppercase tracking-wide text-[var(--color-text-muted)]">Your camera</span>
            <button type="button" aria-label="End video" onClick={(e) => { e.stopPropagation(); setVideoStep("idle"); }} className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-danger)] text-[9px] leading-none text-white">×</button>
          </div>
        )}
      </div>

      {/* Hard divider — everything below is the DEMO explaining the product to you. */}
      <div className="border-t border-dashed border-[var(--color-border)] px-5 pt-3">
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">
          ↑ Only the frame above is on your client&apos;s screen — the cards below explain the product to you
        </p>
      </div>

      {/* The three permission-priming surfaces — demo teaching aids, not the client's screen. */}
      <div data-tour="client-priming" className="grid gap-3 px-5 pb-5 pt-3 sm:grid-cols-3">
        <PrimeCard icon={<Headphones className="h-4 w-4" />} title="Audio" tone={audioEnabled ? "on" : "idle"}>
          {audioEnabled ? "Sound enabled — the tone pans with the ball." : "Client taps once to allow sound (autoplay rule)."}
        </PrimeCard>

        <PrimeCard icon={<Gamepad2 className="h-4 w-4" />} title="Haptics" tone={hapticsStatus === "ready" ? "on" : "idle"}>
          {hapticsStatus === "ready"
            ? `${controllerLabel ?? "Controller"} paired — rumble follows the ball.`
            : hapticsStatus === "unsupported"
              ? "Needs a Chromium browser; this client can still use audio + visual."
              : "Connect a controller and press any button to pair."}
        </PrimeCard>

        <VideoPrimeCard />
      </div>
    </div>
    </>
  );
}

function PrimeCard({ icon, title, tone, children }: { icon: React.ReactNode; title: string; tone: "on" | "idle"; children: React.ReactNode }) {
  return (
    <div className={cn("rounded-xl border p-3.5", tone === "on" ? "border-[var(--color-border-gold)] bg-[var(--color-navy-light)]" : "border-[var(--color-border)] bg-[var(--color-bg-deep)]")}>
      <div className="flex items-center gap-2">
        <span className={cn("flex h-7 w-7 items-center justify-center rounded-md border", tone === "on" ? "border-[var(--color-border-gold)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]")}>
          {icon}
        </span>
        <span className="font-heading text-[14px] font-medium text-[var(--color-text-primary)]">{title}</span>
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-[var(--color-text-muted)]">{children}</p>
    </div>
  );
}

/**
 * Video pre-permission priming card + a clearly-labelled ILLUSTRATION of the native
 * camera prompt. The scope forbids rendering native chrome as if the app controls
 * it — so this is explicitly framed as "an illustration of your client's prompt."
 */
function VideoPrimeCard() {
  const [showPrompt, setShowPrompt] = useState(false);
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-3.5">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-[var(--color-border)] text-[var(--color-text-muted)]">
          <Video className="h-4 w-4" />
        </span>
        <span className="font-heading text-[14px] font-medium text-[var(--color-text-primary)]">Video</span>
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-[var(--color-text-muted)]">
        Your browser will ask to use your camera. It stays on your device — Psy180 never sees it.
      </p>
      {!showPrompt ? (
        <button
          type="button"
          onClick={() => setShowPrompt(true)}
          className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-accent-bright)] hover:text-[var(--color-accent)]"
        >
          <Camera className="h-3 w-3" /> Show the prompt your client sees
        </button>
      ) : (
        <div className="relative mt-3 rounded-lg border border-[#3a4a5e] bg-[#202733] p-3 text-[#e8eef6] shadow-lg">
          <span className="absolute -top-2 left-2 rounded bg-[var(--color-bg-deep)] px-1.5 font-mono text-[8px] uppercase tracking-wide text-[var(--color-text-ghost)]">
            Illustration — not a real dialog
          </span>
          <p className="text-[11px] font-medium">demo.psy180.com wants to</p>
          <p className="mt-1 flex items-center gap-1.5 text-[11px]"><Camera className="h-3 w-3" /> Use your camera</p>
          <div className="mt-2.5 flex justify-end gap-2">
            <span className="rounded border border-[#56627a] px-2.5 py-1 text-[10px]">Block</span>
            <span className="rounded bg-[#3b82f6] px-2.5 py-1 text-[10px] font-medium text-white">Allow</span>
          </div>
        </div>
      )}
    </div>
  );
}
